/* eslint-disable complexity */
import Mark from '@patsnap/mark.js';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    TechnicalFeatureHighlightService,
    TechnicalFeatureMatch,
    HighlightElementConfig,
    HoverEventHandlers,
    PendingFuzzyMatch,
    TwoPhaseHighlightConfig,
} from '../technical-feature-highlight-service';
import type { FuzzyTextMatchApi } from '../../apis/fuzzy-text-match-api';
import { FuzzyTextMatchApi as FuzzyTextMatchApiSymbol } from '../../apis/fuzzy-text-match-api';

/**
 * 技术特征高亮服务实现
 */
@injectable()
export class TechnicalFeatureHighlightServiceImpl implements TechnicalFeatureHighlightService {
    constructor(
        @inject(FuzzyTextMatchApiSymbol)
        private readonly _fuzzyTextMatchApi: FuzzyTextMatchApi,
    ) {}

    /**
     * 调整技术特征文本
     */
    adjustText(text: string): string {
        // 1. 去掉开头和中间的段落号[xxxx]格式
        let processed = text.replace(/\[\d+\]/g, '');

        // 2. 去掉省略号'... '（包括后面可能的空格）
        processed = processed.replace(/\.\.\.\s*/g, '');

        // 3. 去掉首尾空白字符
        processed = processed.trim();

        // 4. 去掉末尾的分号、逗号、句号（支持中英文标点）
        processed = processed.replace(/[；，。;,.]+$/, '');

        return processed;
    }

    /**
     * 高亮单个元素
     */
    highlightElement(config: HighlightElementConfig): void {
        const { element, text, addLetter, letter, color, bgColor, featureIndex } = config;
        const elementText = element.innerText;

        // 设置元素属性
        element.setAttribute('data-highlight-letter', letter);
        element.setAttribute('data-feature-text', text);
        element.setAttribute('data-feature-index', String(featureIndex));
        element.style.backgroundColor = bgColor;

        // 如果需要添加字母标识，使用 CSS 变量和 data 属性，通过 ::before 伪元素显示
        if (addLetter || elementText === text) {
            element.setAttribute('data-show-letter', 'true');
            element.style.setProperty('--letter-bg-color', color);
        }
    }

    /**
     * 清除指定容器中的所有高亮标记
     */
    clearHighlight(container: HTMLElement, handlers?: HoverEventHandlers): void {
        // 先移除事件监听器
        if (handlers) {
            this.detachHoverListeners(container, handlers);
        }

        // 移除所有高亮元素的属性和样式
        const highlightedElements = container.querySelectorAll('[data-highlight-letter]');
        highlightedElements.forEach((element) => {
            element.removeAttribute('data-highlight-letter');
            element.removeAttribute('data-feature-text');
            element.removeAttribute('data-feature-index');
            element.removeAttribute('data-show-letter');
            const htmlElement = element as HTMLElement;
            htmlElement.style.backgroundColor = '';
            htmlElement.style.removeProperty('--letter-bg-color');
        });

        // 使用 Mark.js 的 unmark 移除 <mark> 标签
        const instance = new Mark(container);
        instance.unmark();
    }

    /**
     * 为高亮元素添加 hover 事件监听
     */
    attachHoverListeners(container: HTMLElement, handlers: HoverEventHandlers): void {
        const highlightedElements = container.querySelectorAll('[data-highlight-letter]');
        highlightedElements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.addEventListener('mouseenter', handlers.onMouseEnter);
            htmlElement.addEventListener('mouseleave', handlers.onMouseLeave);
        });
    }

    /**
     * 移除高亮元素的 hover 事件监听
     */
    detachHoverListeners(container: HTMLElement, handlers: HoverEventHandlers): void {
        const highlightedElements = container.querySelectorAll('[data-highlight-letter]');
        highlightedElements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.removeEventListener('mouseenter', handlers.onMouseEnter);
            htmlElement.removeEventListener('mouseleave', handlers.onMouseLeave);
        });
    }

    /**
     * 两阶段高亮技术特征
     */
    async startTwoPhaseHighlight(config: TwoPhaseHighlightConfig): Promise<void> {
        const { container, matches, handlers, callbacks, language } = config;

        if (!matches?.length) {
            console.warn('[TechnicalFeatureHighlightService]: No matched tech features to highlight.');
            return;
        }

        // 先清除旧的高亮
        this.clearHighlight(container, handlers);

        const instance = new Mark(container);
        const containerText = container.textContent || container.innerText || '';

        // ========== 第一���段：精确匹配并高亮 ==========
        const pendingFuzzyMatches = this._processExactMatches({
            instance,
            containerText,
            matches,
            getStyles: config.getStyles,
        });

        // 为第一阶段的高亮元素添加 hover 事件监听
        if (handlers) {
            this.attachHoverListeners(container, handlers);
        }

        // 第一阶段完成回调
        callbacks?.onFirstPhaseComplete?.();

        // ========== 第二阶段：模糊匹配并高亮 ==========
        if (pendingFuzzyMatches.length === 0) {
            callbacks?.onSecondPhaseComplete?.();
            return;
        }
        if (!language) {
            console.error('没有传语言参数,不支持模糊高亮');
            return;
        }

        await this._processFuzzyMatches({
            instance,
            pendingMatches: pendingFuzzyMatches,
            originText: config.originText,
            language: language,
            container,
            handlers,
            callbacks,
        });
    }

    /**
     * 收集并分类匹配项：精确匹配项和待模糊匹配项
     */
    private _collectMatchItems(params: {
        containerText: string;
        matches: TechnicalFeatureMatch[];
        getStyles: (techFeature: string) => { letter: string; color: string; bgColor: string };
    }) {
        const { containerText, matches, getStyles } = params;
        type ExactMatchItem = { text: string; position: number; letter: string; color: string; bgColor: string; featureIndex: number };
        const exactMatchItems: ExactMatchItem[] = [];
        const pendingFuzzyMatches: PendingFuzzyMatch[] = [];
        const lowerContainerText = containerText.toLowerCase();
        matches.forEach((match) => {
            const styles = getStyles(match.techFeature);
            match.comparisonFeature.split('\n').forEach((feature, featureIndex) => {
                const adjustedText = this.adjustText(feature);
                const position = lowerContainerText.indexOf(adjustedText.toLowerCase());
                if (position !== -1) {
                    exactMatchItems.push({ text: adjustedText, position, ...styles, featureIndex });
                } else {
                    pendingFuzzyMatches.push({ techFeature: match.techFeature, text: adjustedText, ...styles, featureIndex });
                }
            });
        });

        return { exactMatchItems, pendingFuzzyMatches };
    }

    /**
     * 处理精确匹配：遍历所有技术特征，精确匹配的立即高亮，无法匹配的加入待处理队列
     * @returns 返回待模糊匹配的项列表
     */
    private _processExactMatches(params: {
        instance: any;
        containerText: string;
        matches: TechnicalFeatureMatch[];
        getStyles: (techFeature: string) => { letter: string; color: string; bgColor: string };
    }): PendingFuzzyMatch[] {
        const { instance, containerText, matches, getStyles } = params;

        // 1. 收集并分类匹配项
        const { exactMatchItems, pendingFuzzyMatches } = this._collectMatchItems({ containerText, matches, getStyles });

        // 2. 按照在原文中出现的位置排序后高亮
        exactMatchItems
            .sort((a, b) => a.position - b.position)
            .forEach((item) => {
                this._highlightText(instance, item.text, {
                    letter: item.letter,
                    color: item.color,
                    bgColor: item.bgColor,
                    featureIndex: item.featureIndex,
                });
            });

        return pendingFuzzyMatches;
    }

    /**
     * 处理模糊匹配：对所有待处理项进行模糊匹配并高亮
     */
    private async _processFuzzyMatches(params: {
        instance: any;
        pendingMatches: PendingFuzzyMatch[];
        originText: string;
        language: 'CN' | 'EN' | 'JP' | 'TW';
        container: HTMLElement;
        handlers?: HoverEventHandlers;
        callbacks?: TwoPhaseHighlightConfig['callbacks'];
    }): Promise<void> {
        const { instance, pendingMatches, originText, language, container, handlers, callbacks } = params;

        // 第二阶段开始回调
        callbacks?.onSecondPhaseStart?.(pendingMatches.length);

        try {
            // 批量处理模糊匹配
            const fuzzyMatchPromises = pendingMatches.map(async (pending) => {
                const matchedText = await this._findFuzzyMatchedText(
                    pending.text,
                    originText,
                    language,
                );

                if (matchedText) {
                    // 模糊匹配成功：高亮匹配到的文本
                    this._highlightText(instance, matchedText, {
                        letter: pending.letter,
                        color: pending.color,
                        bgColor: pending.bgColor,
                        featureIndex: pending.featureIndex,
                    });
                }
            });

            // 等待所有模糊匹配完成
            await Promise.all(fuzzyMatchPromises);

            // 为第二阶段的高亮元素添加 hover 事件监听
            if (handlers) {
                this.attachHoverListeners(container, handlers);
            }

            // 第二阶段完成回调
            callbacks?.onSecondPhaseComplete?.();
        } catch (error) {
            console.error('模糊匹配过程出错:', error);
            callbacks?.onError?.(error as Error);
        }
    }

    /**
     * 查找模糊匹配的文本
     * @returns 返回匹配到的文本，如果未匹配到则返回 null
     */
    private async _findFuzzyMatchedText(
        text: string,
        originText: string,
        language: 'CN' | 'EN' | 'JP' | 'TW',
    ): Promise<string | null> {
        try {
            const data = await this._fuzzyTextMatchApi.findTextInOrigin({
                cell: text,
                cell_lang: language,
                origin_text: originText,
            });

            if (data?.cell_lst?.length > 0) {
                const matchedText = data.cell_lst[0];
                // console.log('模糊匹配成功:', text, '->', matchedText);
                return matchedText;
            }

            console.warn('模糊匹配未找到结果:', text);
            return null;
        } catch (error) {
            console.error('模糊匹配失败:', text, error);
            return null;
        }
    }

    /**
     * 高亮文本（唯一的高亮逻辑入口）
     */
    private _highlightText(
        instance: any,
        text: string,
        styles: { letter: string; color: string; bgColor: string; featureIndex: number },
    ): void {
        let subHighlightTermsIndex = 0;
        instance.mark(text, {
            className: 'technical-feature-highlight-term',
            acrossElements: true,
            separateWordSearch: false,
            each: (element: any) => {
                this.highlightElement({
                    element: element as HTMLElement,
                    text,
                    addLetter: subHighlightTermsIndex === 0,
                    letter: styles.letter,
                    color: styles.color,
                    bgColor: styles.bgColor,
                    featureIndex: styles.featureIndex,
                });
                subHighlightTermsIndex++;
            },
        });
    }
}
